import useSWR from "swr";
import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAvatars from "./DisplayAvatars";
import { useForm } from "./formStore";
import DropdownState from "components/DropdownState";
import Loader from "@/components/ui/Loader";
import MyPagination from "components/ui/MyPagination";
import MyRefetchButton from "components/ui/MyRefetchButton";
import Dropdown from "components/ui/Selector";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fetcher } from "api/swr";
import { ContentLangs } from "config";
import { X } from "lucide-react";

export { useForm } from "./formStore";

const LANG_OPTIONS = ContentLangs.filter((item) => item.code > 0);

const ORDER_OPTIONS = [
  { code: "updateTime", name: "updateTime" },
  { code: "likeCount", name: "likeCount" },
  {
    code: "dailyCount.reCreateCount",
    name: "dailyCount.reCreateCount",
  },
  { code: "reCreateCount", name: "reCreateCount" },
  {
    code: "dailyCount.downloadCount",
    name: "dailyCount.downloadCount",
  },
  { code: "dCount", name: "dCount" },
  { code: "reportCount", name: "reportCount" },
];

const CLIENT_VER_OPTIONS = [{ code: 0, name: "designer(0)" }];
const PAGE_SIZE = 90;

const isClientVerSelected = (value) => value === 0 || value === "0";

const parseAvatarIds = (value) =>
  [
    ...new Set(
      String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0),
    ),
  ];

const fetchAvatarsByIds = ([, ids]) =>
  fetcher(["/avatars/batch", { ids: ids.join(",") }]);

const Index = () => {
  //TODO: taglibs should be fetched in the background
  const { mutate: refetch } = useSWR(["/tagLibs"]);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    afters,
    after,
    goAfter,
    lang,
    updateLang,
    order,
    updateOrder,
    state,
    updateState,
    clientVer,
    updateClientVer,
    tag,
    updateTag,
    id,
    updateId,
    tid,
    updateTid,
    userId,
    updateUserId,
    aid,
    updateAid,
    filterByUserId,
    filterByAid,
  } = useForm((s) => s);

  const avatarIds = useMemo(() => parseAvatarIds(id), [id]);
  const isMultiIdQuery = avatarIds.length > 1;

  const listParams = useMemo(() => {
    const isRemix = tag === "Remix";
    return {
      // 未选国家时不传 lang（全部）
      lang: lang > 0 ? lang : null,
      order,
      state,
      // 未选时不传 clientVer（全部）；选 designer(0) 时传 0
      clientVer: isClientVerSelected(clientVer) ? 0 : null,
      // Remix 不按 tag 查，而是查 simple=1；未选时都不传
      tag: isRemix ? null : tag || null,
      simple: isRemix ? 1 : null,
      after,
      id: avatarIds[0] || "",
      tid: tid || null,
      author: userId.trim() || null,
      aid: aid.trim() || null,
      limit: PAGE_SIZE,
    };
  }, [lang, order, state, clientVer, tag, after, avatarIds, tid, userId, aid]);

  const tidFromUrl = searchParams.get("tid") || "";

  useEffect(() => {
    if (tidFromUrl !== tid) {
      updateTid(tidFromUrl);
    }
  }, [tidFromUrl]);

  const handleTidChange = (value) => {
    updateTid(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set("tid", value);
        } else {
          next.delete("tid");
        }
        return next;
      },
      { replace: true },
    );
  };

  const clearTidFromUrl = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tid");
        return next;
      },
      { replace: true },
    );
  };

  const handleAuthorClick = (authorId) => {
    clearTidFromUrl();
    filterByUserId(authorId);
  };

  const handleAidClick = (avatarId) => {
    clearTidFromUrl();
    filterByAid(avatarId);
  };

  const listQuery = useSWR(
    isMultiIdQuery ? null : ["/avatars", listParams],
    fetcher,
  );
  const multiIdQuery = useSWR(
    isMultiIdQuery ? ["/avatars/by-ids", avatarIds] : null,
    fetchAvatarsByIds,
  );

  const {
    data: avatars,
    isLoading,
    isValidating,
    mutate,
  } = isMultiIdQuery ? multiIdQuery : listQuery;

  useEffect(() => {
    refetch();
  }, [refetch]);

  const nextAfter =
    avatars?.length >= PAGE_SIZE ? avatars[avatars.length - 1]?.id : undefined;
  const isLoadingData = isLoading || isValidating;

  return (
    <div className="flex h-[calc(100vh-98px)] flex-col space-y-4">
      <div className="flex-shrink-0">
        <h2 className="text-3xl font-bold tracking-tight">Avatars</h2>
      </div>
      {isLoadingData && <Loader />}
      {!isLoadingData && (
        <>
          <div className="flex w-full flex-shrink-0 flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex shrink-0 items-center gap-4">
              {!isMultiIdQuery && (
                <MyPagination
                  nextAfter={nextAfter}
                  after={after}
                  goAfter={goAfter}
                  afters={afters}
                  className="m-0 w-auto"
                />
              )}
              <MyRefetchButton onClick={mutate} />
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="id-filter"
                className="shrink-0 text-sm font-medium"
              >
                ID:
              </label>
              <div className="relative">
                <Input
                  id="id-filter"
                  type="text"
                  placeholder="Avatar ID/IDs，多个用逗号分隔"
                  value={id}
                  onChange={(e) => updateId(e.target.value)}
                  className="w-[28rem] max-w-full"
                />
                {id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateId("")}
                    className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="aid-filter"
                className="shrink-0 text-sm font-medium"
              >
                AID:
              </label>
              <div className="relative">
                <Input
                  id="aid-filter"
                  type="text"
                  placeholder="Parent Avatar ID"
                  value={aid}
                  onChange={(e) => updateAid(e.target.value)}
                  className="w-80"
                />
                {aid && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateAid("")}
                    className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="tid-filter"
                className="shrink-0 text-sm font-medium"
              >
                TID:
              </label>
              <div className="relative">
                <Input
                  id="tid-filter"
                  type="text"
                  placeholder="Template ID"
                  value={tid}
                  onChange={(e) => handleTidChange(e.target.value)}
                  className="w-48"
                />
                {tid && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleTidChange("")}
                    className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="user-id-filter"
                className="shrink-0 text-sm font-medium"
              >
                UserId:
              </label>
              <div className="relative">
                <Input
                  id="user-id-filter"
                  type="text"
                  placeholder="User ID"
                  value={userId}
                  onChange={(e) => updateUserId(e.target.value)}
                  className="w-80"
                />
                {userId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateUserId("")}
                    className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="lang-filter"
                className="shrink-0 text-sm font-medium"
              >
                Lang:
              </label>
              <div className="relative">
                <Dropdown
                  datas={LANG_OPTIONS}
                  showLabel
                  showCode
                  labelName="Lang"
                  value={lang}
                  update={updateLang}
                  placeholder="All"
                />
                {lang > 0 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    title="Clear lang"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      updateLang(null);
                    }}
                    className="absolute right-8 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="order-filter"
                className="shrink-0 text-sm font-medium"
              >
                Order:
              </label>
              <Dropdown
                datas={ORDER_OPTIONS}
                showLabel
                labelName="Order"
                value={order}
                update={updateOrder}
              />
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="state-filter"
                className="shrink-0 text-sm font-medium"
              >
                State:
              </label>
              <DropdownState value={state} update={updateState} />
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="client-ver-filter"
                className="shrink-0 text-sm font-medium"
              >
                Client Version:
              </label>
              <div className="relative">
                <Dropdown
                  datas={CLIENT_VER_OPTIONS}
                  showLabel
                  labelName="ClientVer"
                  value={clientVer}
                  update={updateClientVer}
                  placeholder="All"
                />
                {isClientVerSelected(clientVer) ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    title="Clear clientVer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      updateClientVer(null);
                    }}
                    className="absolute right-8 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="tag-filter"
                className="shrink-0 text-sm font-medium"
              >
                Tag:
              </label>
              <div className="relative">
                <Dropdown
                  datas={[{ code: "Remix", name: "Remix" }]}
                  showLabel
                  labelName="Tag"
                  value={tag}
                  update={updateTag}
                  placeholder="All"
                />
                {tag ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    title="Clear tag"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      updateTag(null);
                    }}
                    className="absolute right-8 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
          {avatars?.length <= 0 && <div>no avatars</div>}
          {avatars?.length > 0 && (
            <div className="min-h-0 flex-1">
              <DisplayAvatars
                avatars={avatars}
                fillContainer
                onAuthorClick={handleAuthorClick}
                onAidClick={handleAidClick}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Index;
